import { useState, type ReactNode } from 'react';
import { Info } from 'lucide-react';

/**
 * Lightweight click-to-open tooltip — no new dependency (the customer portal
 * doesn't carry @radix-ui/react-tooltip; ops does). Same click/blur-toggle
 * pattern as `apps/customer/src/components/shared/CarbonClaim.tsx`'s inline
 * "About this estimate" tooltip.
 *
 * SIL-252 T16: justifies the scenario builder's assumption defaults (ITAD %,
 * 0.50×TDP power fallback, FMV band) inline — finance buyers distrust
 * unexplained numbers (spec §5, §9 Wave 4).
 */
export function InfoTooltip({
  label,
  children,
  align = 'left',
}: {
  label: string;
  children: ReactNode;
  align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className="text-zinc-400 hover:text-zinc-600"
      >
        <Info className="size-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute bottom-full z-30 mb-2 w-64 rounded-md border border-zinc-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-zinc-700 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {children}
        </span>
      )}
    </span>
  );
}
